/**
 * Hooks para los dashboards — Fase 7.2.
 *
 * Combinan agregaciones desde las vistas y tablas existentes:
 *   - vw_cuenta_corriente_resumen (Fase 7.1) → totales agregados
 *   - vw_recaudacion_mensual (Fase 7.2)      → serie temporal
 *   - empresas / afiliados / transacciones    → cuentas rápidas
 *
 * Todo va directo a Supabase con RLS aplicada (decisión #014).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

// ── KPIs Admin ────────────────────────────────────────────────────────────────

export interface KpisAdmin {
  empresas_activas: number;
  afiliados_activos: number;
  cupones_pendientes: number;
  cupones_vencidos: number;
  transacciones_pre_confirmadas: number;
  total_adeudado: number;
  recaudacion_mes_actual: number;
}

export function useKpisAdmin() {
  return useQuery({
    queryKey: ['dashboard', 'kpis-admin'],
    queryFn: async (): Promise<KpisAdmin> => {
      // Hacemos las consultas en paralelo
      const [
        empresas, afiliados, cupones, transacciones, resumen, recaudacion,
      ] = await Promise.all([
        supabase.from('empresas').select('id', { count: 'exact', head: true }).eq('estado', 'activa'),
        supabase.from('afiliados').select('id', { count: 'exact', head: true }).eq('estado', 'activo'),
        supabase.from('cupones').select('id, estado'),
        supabase.from('transacciones_recaudacion')
          .select('id', { count: 'exact', head: true })
          .in('estado', ['pre_confirmada', 'sin_identificar']),
        supabase.from('vw_cuenta_corriente_resumen').select('saldo_total'),
        supabase.from('vw_recaudacion_mensual').select('mes, monto_acreditado'),
      ]);

      if (empresas.error)      throw empresas.error;
      if (afiliados.error)     throw afiliados.error;
      if (cupones.error)       throw cupones.error;
      if (transacciones.error) throw transacciones.error;
      if (resumen.error)       throw resumen.error;
      if (recaudacion.error)   throw recaudacion.error;

      const cuponesData = (cupones.data ?? []) as Array<{ estado: string }>;
      const pendientes = cuponesData.filter((c) => c.estado === 'pendiente').length;
      const vencidos   = cuponesData.filter((c) => c.estado === 'vencido').length;

      const totalAdeudado = (resumen.data ?? [])
        .reduce((sum, r) => sum + Number((r as { saldo_total: number }).saldo_total ?? 0), 0);

      const mesActual = new Date().toISOString().slice(0, 7); // yyyy-mm
      const recMesActual = (recaudacion.data ?? [])
        .find((r) => (r as { mes: string }).mes === mesActual);
      const recaudacionMes = recMesActual
        ? Number((recMesActual as { monto_acreditado: number }).monto_acreditado)
        : 0;

      return {
        empresas_activas:              empresas.count ?? 0,
        afiliados_activos:             afiliados.count ?? 0,
        cupones_pendientes:            pendientes,
        cupones_vencidos:              vencidos,
        transacciones_pre_confirmadas: transacciones.count ?? 0,
        total_adeudado:                totalAdeudado,
        recaudacion_mes_actual:        recaudacionMes,
      };
    },
    staleTime: 60_000,
  });
}

// ── KPIs Empresa ──────────────────────────────────────────────────────────────

export interface KpisEmpresa {
  saldo_total: number;
  cupones_pendientes: number;
  cupones_vencidos: number;
  proximo_vencimiento: string | null;
  monto_pagado_total: number;
}

export function useKpisEmpresa(empresaId: string | undefined) {
  return useQuery({
    queryKey: ['dashboard', 'kpis-empresa', empresaId],
    queryFn: async (): Promise<KpisEmpresa> => {
      const [resumenRes, cuponesRes] = await Promise.all([
        supabase
          .from('vw_cuenta_corriente_resumen')
          .select('saldo_total, cant_pendientes, cant_vencidos, monto_pagado_total')
          .eq('empresa_id', empresaId!)
          .maybeSingle(),
        supabase
          .from('cupones')
          .select('fecha_vencimiento')
          .eq('empresa_id', empresaId!)
          .in('estado', ['pendiente', 'vencido'])
          .order('fecha_vencimiento', { ascending: true })
          .limit(1),
      ]);

      if (resumenRes.error) throw resumenRes.error;
      if (cuponesRes.error) throw cuponesRes.error;

      const resumen = resumenRes.data as
        | { saldo_total: number; cant_pendientes: number; cant_vencidos: number; monto_pagado_total: number }
        | null;
      const proximo = (cuponesRes.data ?? [])[0] as { fecha_vencimiento: string } | undefined;

      return {
        saldo_total:         Number(resumen?.saldo_total ?? 0),
        cupones_pendientes:  Number(resumen?.cant_pendientes ?? 0),
        cupones_vencidos:    Number(resumen?.cant_vencidos ?? 0),
        proximo_vencimiento: proximo?.fecha_vencimiento ?? null,
        monto_pagado_total:  Number(resumen?.monto_pagado_total ?? 0),
      };
    },
    enabled: !!empresaId,
    staleTime: 60_000,
  });
}

// ── Serie mensual para el gráfico ─────────────────────────────────────────────

export interface RecaudacionMesRow {
  mes: string;
  monto_emitido: number;
  monto_acreditado: number;
  cant_cupones: number;
  cant_pagos: number;
}

export function useRecaudacionMensual() {
  return useQuery({
    queryKey: ['dashboard', 'recaudacion-mensual'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_recaudacion_mensual')
        .select('*');
      if (error) throw error;
      return (data ?? []) as RecaudacionMesRow[];
    },
    staleTime: 5 * 60_000,
  });
}

// ── Top deudores (limitado, para el card del Dashboard Admin) ─────────────────

export interface TopDeudor {
  empresa_id: string;
  empresa_razon_social: string;
  empresa_cuit: string;
  saldo_total: number;
  cant_vencidos: number;
}

export function useTopDeudores(limit = 5) {
  return useQuery({
    queryKey: ['dashboard', 'top-deudores', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vw_cuenta_corriente_resumen')
        .select('empresa_id, empresa_razon_social, empresa_cuit, saldo_total, cant_vencidos')
        .gt('saldo_total', 0)
        .order('saldo_total', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as TopDeudor[];
    },
    staleTime: 60_000,
  });
}
